import { ref, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import { isSortOrder, type SortOrder } from '../types/sort.types';
import { firstQueryValue, parseQuantityFromQuery } from './queryValue';
import useSearchUrlSync from './useSearchUrlSync';
import useUpdateUrl from './useUpdateUrl';

interface ListQuantityOptions {
  /** The page sizes the screen offers; anything else in the URL is discarded. */
  options: readonly number[];
  /** The size the screen opens with, and the one that is kept out of the URL. */
  default: number;
  queryKey?: string;
}

interface ListUrlSyncOptions {
  fields: readonly string[];
  defaultField: string;
  fieldQueryKey?: string;
  validSorts?: readonly string[];
  defaultSort?: string;
  defaultOrder?: SortOrder | '';
  neutralOrder?: SortOrder;
  /**
   * Opt-in: when given, the page size is hydrated from the URL and written back by
   * `syncUrl`. Screens that leave it out own their own page size, and it never
   * reaches the query.
   */
  quantity?: ListQuantityOptions;
}

type ExtraQuery = Record<string, string | number | boolean | Date | null | undefined>;

interface ListUrlSyncResult {
  searchTerm: Ref<string>;
  searchFieldId: Ref<string>;
  sort: Ref<string>;
  order: Ref<SortOrder | ''>;
  currentPage: Ref<number>;
  syncUrl: (extraQuery?: ExtraQuery) => void;
}

/**
 * Configuring `quantity` is what puts the page size under this composable: only then
 * `itemsPerPage` is part of the result, so a screen cannot read a size the URL never
 * hydrated.
 */
function useListUrlSync(
  options: ListUrlSyncOptions & { quantity: ListQuantityOptions }
): ListUrlSyncResult & { itemsPerPage: Ref<number> };
function useListUrlSync(options: ListUrlSyncOptions): ListUrlSyncResult;
function useListUrlSync(options: ListUrlSyncOptions) {
  const {
    fields,
    defaultField,
    fieldQueryKey = 'searchField',
    validSorts,
    defaultSort = '',
    defaultOrder = '',
    neutralOrder,
    quantity,
  } = options;

  const route = useRoute();
  const { updateUrl } = useUpdateUrl();
  const { searchTerm, searchFieldId } = useSearchUrlSync({ fields, defaultField, fieldQueryKey });

  const rawSort = firstQueryValue(route.query.sort);
  const rawOrder = firstQueryValue(route.query.order);

  const resolvedSort =
    rawSort && (!validSorts || validSorts.includes(rawSort)) ? rawSort : defaultSort;
  const resolvedOrder: SortOrder | '' = isSortOrder(rawOrder) ? rawOrder : defaultOrder;
  const hasSort = Boolean(resolvedSort) && isSortOrder(resolvedOrder);

  const isNeutralOrder = (value: SortOrder | '') => value !== '' && value === neutralOrder;

  const sort = ref(hasSort ? resolvedSort : '');
  const order = ref<SortOrder | ''>(hasSort ? (resolvedOrder as SortOrder) : '');

  const rawPage = Number(firstQueryValue(route.query.page));
  const currentPage = ref(Number.isInteger(rawPage) && rawPage > 1 ? rawPage : 1);

  const quantityQueryKey = quantity?.queryKey ?? 'itemsPerPage';
  const itemsPerPage = ref(
    quantity
      ? parseQuantityFromQuery(route.query[quantityQueryKey], quantity.options, quantity.default)
      : 0
  );

  const syncUrl = (extraQuery: ExtraQuery = {}) => {
    const trimmed = searchTerm.value.trim();

    void updateUrl({
      search: trimmed,
      page: currentPage.value,
      sort:
        sort.value && order.value && !isNeutralOrder(order.value)
          ? { sort: sort.value, order: order.value as SortOrder }
          : undefined,
      query: {
        ...(trimmed ? { [fieldQueryKey]: searchFieldId.value } : {}),
        ...(quantity && itemsPerPage.value !== quantity.default
          ? { [quantityQueryKey]: itemsPerPage.value }
          : {}),
        ...extraQuery,
      },
    });
  };

  return { searchTerm, searchFieldId, sort, order, currentPage, itemsPerPage, syncUrl };
}

export default useListUrlSync;
